import {
  Autocomplete,
  autocompleteClasses,
  Button,
  Dialog,
  DialogActions,
  FormControl,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
  PopperProps,
  Stack,
  styled,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { ColoredAvatar, DynamicIcon } from 'src/components';
import { PartnerOption, usePartnersDialogOptions } from './use-partners-dialog-options';

enum PartnersDialogTabs {
  all = 'all',
  current = 'current',
}

interface SelectPartnerDialogProps {
  open: boolean;
  onChange: (option: PartnerOption) => void;
  onClose: () => void;
  canCreatePartner?: boolean;
  onCreatePartner?: () => void;
}

const StyledAutocompletePopper = styled(Stack)(({ theme }) => ({
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(0.5),
    width: '100%',
    position: theme.utils.important('relative'),
  },
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    borderRadius: 0,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    maxHeight: 'none',
  },
}));

const RelativePopper = ({
  anchorEl,
  disablePortal,
  style,
  children,
  ...props
}: PopperProps) => {
  return (
    <StyledAutocompletePopper {...props}>
      {children as React.ReactNode}
    </StyledAutocompletePopper>
  );
};

const tabs = [
  {
    value: PartnersDialogTabs.all,
    label: 'All',
  },
  {
    value: PartnersDialogTabs.current,
    label: 'Current',
  },
];

export const SelectPartnerDialog = ({
  open,
  onChange,
  onClose,
  canCreatePartner = false,
  onCreatePartner,
}: SelectPartnerDialogProps) => {
  const [activeTab, setActiveTab] = useState(PartnersDialogTabs.current);

  const { partnersOptions, filterFn } = usePartnersDialogOptions({
    currentPartner: activeTab === PartnersDialogTabs.current,
  });
  const [value, setValue] = useState<PartnerOption | null>(null);

  return (
    <Dialog data-testid="select-partner-dialog" open={open} onClose={onClose}>
      <Stack
        sx={{
          padding: 0.5,
          height: '35rem',
          width: '20rem',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue as PartnersDialogTabs)}
        >
          {tabs.map((el) => (
            <Tab key={el.label} value={el.value} label={el.label} />
          ))}
        </Tabs>
        <FormControl variant="standard" sx={{ width: '100%', flex: 1, overflow: 'auto' }}>
          <Autocomplete
            aria-label="Select Partner"
            popupIcon={null}
            autoHighlight
            open
            disablePortal
            value={value}
            onChange={(_, newValue) => {
              if (newValue) {
                setValue(newValue);
                onChange(newValue);
                onClose();
              }
            }}
            sx={{
              padding: 1,
            }}
            PopperComponent={RelativePopper}
            renderInput={(params) => (
              <TextField
                variant="standard"
                placeholder="Select Partner"
                {...params}
                autoFocus
                inputProps={{
                  ...params.inputProps,
                  'data-testid': 'select-partner-search',
                }}
                InputProps={{
                  disableUnderline: false,
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <DynamicIcon name="search" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            options={partnersOptions}
            filterOptions={filterFn}
            getOptionLabel={(option) => option.label}
            renderOption={(props, option) => (
              <ListItem {...props} key={option.id}>
                <ListItemIcon>
                  {option.icon ? (
                    <DynamicIcon name={option.icon} />
                  ) : (
                    <ColoredAvatar name={option.label} />
                  )}
                </ListItemIcon>
                <ListItemText primary={option.label} />
              </ListItem>
            )}
            isOptionEqualToValue={(option, value) =>
              option.id === value.id && option.label === value.label
            }
          />
        </FormControl>
        {canCreatePartner ? (
          <DialogActions
            sx={{
              justifyContent: 'center',
              padding: 1,
            }}
          >
            <Button
              data-testid="select-partner-create"
              variant="text"
              onClick={onCreatePartner}
              sx={{
                minWidth: '12rem',
                flexGrow: 1,
              }}
            >
              Create partner
            </Button>
          </DialogActions>
        ) : null}
      </Stack>
    </Dialog>
  );
};
